import React, { useEffect, useState } from "react";
import styles from "./Theme.module.css";
import ColorPicker from "./ColorPicker";
import AxiosUrl from "../AxiosUrl";
import { ToastContainer, toast } from "react-toastify";

// The store's colours (GET /get-color, POST /set-color). The first four cards
// are the colours every store has; the cards under "More colours" are
// optional: a colour left on Automatic is worked out by the store from its
// other colours. Each optional colour lists the colour its picker starts
// from when the seller chooses one. "Product page" (MOBILE_CARDS below)
// has colours with defaults of their own.
const CARDS = [
  { title: "Header Color", colors: [["HeaderColor", "Header Background"], ["HeaderTextColor", "Header Text"]] },
  { title: "Footer Color", colors: [["FooterColor", "Footer Background"], ["FooterTextColor", "Footer Text"]] },
  { title: "Add To Cart Color", colors: [["AddToCartColor", "AddToCart Background"], ["AddToCartTextColor", "AddToCart Text"]] },
  { title: "Buy Now Color", colors: [["BuyNowColor", "BuyNow Background"], ["BuyNowTextColor", "BuyNow Text"]] },
];
const OPTIONAL_CARDS = [
  { title: "Top Bar Color", hint: "The thin bar above the header (delivery charge, phone number).", colors: [["TopBarColor", "Top Bar Background", "HeaderColor"], ["TopBarTextColor", "Top Bar Text", "HeaderTextColor"]] },
  { title: "Cart Bar Color", hint: "The bar with the cart total that shows while scrolling.", colors: [["CartBarColor", "Cart Bar Background", "HeaderColor"], ["CartBarTextColor", "Cart Bar Text", "HeaderTextColor"]] },
  { title: "Menu Hover Color", hint: "Header menu items under the mouse.", colors: [["HeaderHoverColor", "Menu Hover Background", "HeaderColor"], ["HeaderHoverTextColor", "Menu Hover Text", "HeaderTextColor"]] },
  { title: "Badge Color", hint: "The product code tag and the OFFER label.", colors: [["BadgeBgColor", "Badge Background", "BuyNowColor"], ["BadgeTextColor", "Badge Text", "BuyNowTextColor"]] },
  { title: "Accent & Price Color", hint: "Accent: section headings, links, selected items and borders. Price: product prices.", colors: [["AccentColor", "Accent", "BuyNowColor"], ["PriceColor", "Price", "BuyNowColor"]], preview: (c) => ({ color: c.AccentColor || undefined }) },
  { title: "Background Color", hint: "Page: behind the whole page. Section: behind product rows, category pages and product details.", colors: [["BodyBgColor", "Page Background", "#ffffff"], ["SectionBgColor", "Section Background", "#f5f5f5"]], preview: (c) => ({ backgroundColor: c.BodyBgColor || undefined }) },
];
// Product pages: the Call / WhatsApp and Messenger buttons, and the colour
// layout's OFFER box, phone cards and cart pop-up. Each colour starts at the
// theme's own (the third value), and a colour left at it is saved as not
// set, so the store keeps the default.
const MOBILE_CARDS = [
  { title: "Call & WhatsApp Buttons", hint: "The phone number and WhatsApp buttons on product pages.", colors: [["CallColor", "Call & WhatsApp Background", "#008000"], ["CallTextColor", "Call & WhatsApp Text", "#ffffff"]] },
  { title: "Messenger Button", hint: "The Messenger button on product pages.", colors: [["MessengerColor", "Messenger Background", "#1569dd"], ["MessengerTextColor", "Messenger Text", "#ffffff"]] },
  { title: "Offer Box Color", hint: "The OFFER box above the colour cards.", colors: [["MobileOfferBgColor", "Offer Box Background", "#782323"], ["MobileOfferTextColor", "Offer Box Text", "#ffffff"]] },
  { title: "Offer Label & Card Border", hint: "The OFFER label, and the border of the OFFER box and of each colour card.", colors: [["MobileOfferTagColor", "Offer Label", "#b22222c4"], ["MobileCardBorderColor", "Card Border", "#ff5f5f"]], preview: (c) => ({ backgroundColor: c.MobileOfferTagColor, color: c.MobileOfferTextColor }) },
  { title: "Order Button Color", hint: "The অর্ডার করুন button on each colour card.", colors: [["MobileOrderColor", "Order Button Background", "#c9151b"], ["MobileOrderTextColor", "Order Button Text", "#ffffff"]] },
  { title: "Cart Button Color", hint: "The কার্ট-এ যোগ করুন button on each colour card.", colors: [["MobileCartColor", "Cart Button Background", "#b8561b"], ["MobileCartTextColor", "Cart Button Text", "#ffffff"]] },
  { title: "Price Band & Button Strip", hint: "The band with the colour name and price on each photo, and the strip behind the two buttons.", colors: [["MobilePriceBandColor", "Price Band", "#543627"], ["MobileStripColor", "Button Strip", "#9000001a"]], preview: (c) => ({ backgroundColor: c.MobilePriceBandColor, color: "#ffffff" }) },
  { title: "Pop-up Order Now! Button", hint: "The cart pop-up's Order Now! button.", colors: [["MobilePopupOrderColor", "Order Now! Background", "#dc143c"], ["MobilePopupOrderTextColor", "Order Now! Text", "#ffffff"]] },
  { title: "Pop-up আরও কিনব Button", hint: "The cart pop-up's আরও কিনব button.", colors: [["MobilePopupMoreColor", "আরও কিনব Background", "#6495ed"], ["MobilePopupMoreTextColor", "আরও কিনব Text", "#ffffff"]] },
  { title: "Pop-up − / + Buttons", hint: "The cart pop-up's − / + buttons.", colors: [["MobilePopupCountColor", "− / + Background", "#8665ff9c"], ["MobilePopupCountTextColor", "− / + Text", "#ffffff"]] },
  { title: "Pop-up Border", hint: "The cart pop-up's border on phones.", colors: [["MobilePopupBorderColor", "Pop-up Border", "#f66800e5"]], preview: (c) => ({ borderBottom: `4px solid ${c.MobilePopupBorderColor}` }) },
];
const DEFAULTS = {
  HeaderColor: "#fff", HeaderTextColor: "#000", FooterColor: "#fff", FooterTextColor: "#000",
  AddToCartColor: "#fff", AddToCartTextColor: "#000", BuyNowColor: "#fff", BuyNowTextColor: "#000",
};
const MOBILE_DEFAULTS = Object.fromEntries(MOBILE_CARDS.flatMap((card) => card.colors.map(([key, , value]) => [key, value])));
const ALL_KEYS = [...CARDS, ...OPTIONAL_CARDS, ...MOBILE_CARDS].flatMap((card) => card.colors.map(([key]) => key));
const isDefault = (key, value) => !!MOBILE_DEFAULTS[key] && String(value).toLowerCase() === MOBILE_DEFAULTS[key].toLowerCase();
// a card's title bar in its own colours (background + text)
const pairPreview = (card, c) => ({ backgroundColor: c[card.colors[0][0]] || undefined, color: c[card.colors[1][0]] || undefined });
const inRows = (list) => list.reduce((rows, card, i) => (i % 2 ? rows[rows.length - 1].push(card) : rows.push([card]), rows), []);

function Theme() {
  const [colors, setColors] = useState(DEFAULTS);
  const [dataLoading, setDataLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const uid = localStorage.getItem("id");
  const setColor = (key, value) => setColors((c) => ({ ...c, [key]: value }));

  useEffect(() => {
    setDataLoading(true);
    AxiosUrl.get(`/get-color/${uid}`)
      .then((res) => {
        // ("No Data Found" for a store that never saved its colours)
        const saved = (res.data && res.data.data && res.data.data[0]) || {};
        setColors(Object.fromEntries(ALL_KEYS.map((key) => [key, saved[key] || DEFAULTS[key] || MOBILE_DEFAULTS[key] || ""])));
      })
      .catch((error) => {
        console.log(error);
        // no pickers: saving the defaults would replace the store's colours
        setLoadFailed(true);
      })
      .finally(() => {
        setDataLoading(false);
      });
  }, []);

  const saveTheme = () => {
    setLoading(true);
    // (a product-page colour at its default is sent empty: not set)
    const body = Object.fromEntries(Object.entries(colors).map(([key, value]) => [key, isDefault(key, value) ? "" : value]));
    AxiosUrl.post(`/set-color/${uid}`, body)
      .then((res) => {
        toast.success(res.data);
      })
      .catch((error) => {
        console.log(error);
        toast.error("Could not save the colours. Please try again.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const renderColorPickerSkeleton = () => (
    <div style={{padding:'20px',display:'flex',flexDirection:'column',gap:'16px'}}>
      {[0,1].map(p => (
        <div key={p} style={{display:'flex',alignItems:'center',gap:'12px'}}>
          <div className={styles.skeleton} style={{width:'36px',height:'36px',borderRadius:'50%',flexShrink:0}}></div>
          <div style={{flex:1}}>
            <div className={styles.skeleton} style={{width:'120px',height:'14px',marginBottom:'8px'}}></div>
            <div className={styles.skeleton} style={{width:'100%',height:'36px',borderRadius:'6px'}}></div>
          </div>
        </div>
      ))}
      <div style={{display:'flex',justifyContent:'center',paddingTop:'8px'}}>
        <div className={styles.skeleton} style={{width:'160px',height:'42px',borderRadius:'6px'}}></div>
      </div>
    </div>
  );

  // kind: "classic", "optional" (Automatic when empty) or "default" (product page)
  const renderCard = (card, kind) => {
    const optional = kind === "optional";
    const auto = optional && card.colors.every(([key]) => !colors[key]);
    return (
      <div className={styles.ThemeHeaderFooter} key={card.title}>
        <h3 style={dataLoading || loadFailed ? {} : (card.preview ? card.preview(colors) : pairPreview(card, colors))}>
          {card.title}
          {!dataLoading && !loadFailed && auto && <span className={styles.autoTag}>Automatic</span>}
        </h3>
        {card.hint && <p className={styles.cardHint}>{card.hint}</p>}
        {dataLoading ? renderColorPickerSkeleton() : loadFailed ? (
          <p className={styles.loadFailed}>Could not load your colours. Please refresh the page.</p>
        ) : (
          <>
            {card.colors.map(([key, label, extra]) => (
              <ColorPicker
                key={key}
                name={label}
                SetColor={(value) => setColor(key, value)}
                C_Color={colors[key]}
                optional={optional}
                startColor={optional ? colors[extra] || extra : undefined}
                defaultColor={kind === "default" ? extra : null}
              />
            ))}
            <button className={styles.saveTheme} onClick={saveTheme} disabled={loading}>
              {loading ? "Loading..." : "Save Theme Color"}
            </button>
          </>
        )}
      </div>
    );
  };

  return (
    <div className={styles.mainTheme}>
      <ToastContainer />
      {inRows(CARDS).map((row, i) => (
        <div className={styles.ThemeContainer} key={`c${i}`}>{row.map((card) => renderCard(card, "classic"))}</div>
      ))}
      <div className={styles.sectionHead}>
        <h2 className={styles.sectionTitle}>More colours</h2>
        <p className={styles.sectionHint}>Optional. A colour left on Automatic follows your header and button colours.</p>
      </div>
      {inRows(OPTIONAL_CARDS).map((row, i) => (
        <div className={styles.ThemeContainer} key={`o${i}`}>{row.map((card) => renderCard(card, "optional"))}</div>
      ))}
      <div className={styles.sectionHead}>
        <h2 className={styles.sectionTitle}>Product page</h2>
        <p className={styles.sectionHint}>The Call, WhatsApp and Messenger buttons; for the colour layout, the OFFER box, the colour cards on phones and the cart pop-up. Each colour starts at the theme's own.</p>
      </div>
      {inRows(MOBILE_CARDS).map((row, i) => (
        <div className={styles.ThemeContainer} key={`m${i}`}>{row.map((card) => renderCard(card, "default"))}</div>
      ))}
    </div>
  );
}

export default Theme;
